import { useState, useEffect, useCallback } from "react";
import { useAdminData } from "../../AdminDataContext";
import { orgStructureStore, type OrgNode } from "../../utils/orgStructureStore";
import { fetchOrgStructureJson, putOrgStructureJson } from "../../../../api/orgStructure";
import { reportError } from "../../../../api/errors";

interface EditModalProps {
  node: OrgNode;
  onSave: (node: OrgNode) => void;
  onClose: () => void;
}

const DUTY_MAX_LEN = 100;

function EditModal({ node, onSave, onClose }: EditModalProps) {
  const { members } = useAdminData();
  const [title, setTitle] = useState(node.title);
  const [name, setName] = useState(node.name);
  const [nameSearch, setNameSearch] = useState(node.name);
  const [description, setDescription] = useState(node.description ?? "");
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    setTitle(node.title);
    setName(node.name);
    setNameSearch(node.name);
    setDescription(node.description ?? "");
  }, [node.id, node.title, node.name, node.description]);

  const filteredMembers = members.filter(
    (m) => !nameSearch || m.realName.includes(nameSearch) || m.name.includes(nameSearch)
  );

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="font-bold text-gray-800 mb-5">编辑成员信息</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">职位名称</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              姓名 <span className="text-gray-400 font-normal">（从会员名单选择）</span>
            </label>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"></i>
              <input
                type="text"
                value={nameSearch}
                onChange={(e) => { setNameSearch(e.target.value); setName(e.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                placeholder="搜索会员姓名..."
                className="w-full pl-9 pr-4 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 pl-9"
              />
              {showDropdown && filteredMembers.length > 0 && (
                <div className="absolute z-10 top-full mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                  {filteredMembers.slice(0, 6).map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => { setName(m.realName); setNameSearch(m.realName); setShowDropdown(false); }}
                      className="w-full px-4 py-2.5 text-left hover:bg-gray-50 cursor-pointer transition-colors flex items-center gap-3"
                    >
                      <div className="w-7 h-7 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-red-600">
                        {m.realName.slice(0, 1)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{m.realName}</p>
                        <p className="text-xs text-gray-400">{m.level}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">职责</label>
            <textarea
              value={description}
              maxLength={DUTY_MAX_LEN}
              rows={4}
              placeholder="简要填写该职位主要职责，将同步至会员端协会介绍中的组织架构"
              onChange={(e) => setDescription(e.target.value.slice(0, DUTY_MAX_LEN))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 resize-y min-h-[88px]"
            />
            <p className="text-[10px] text-gray-400 mt-1 text-right">{description.length}/{DUTY_MAX_LEN}</p>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
          <button
            type="button"
            onClick={() =>
              onSave({
                ...node,
                title,
                name,
                description: description.trim() || undefined,
              })
            }
            className="px-4 py-2 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer whitespace-nowrap"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
}

function OrgNodeCard({ node, onEdit, onAdd, onDelete, depth = 0 }: {
  node: OrgNode;
  onEdit: (node: OrgNode) => void;
  onAdd: (parentId: string) => void;
  onDelete: (id: string) => void;
  depth?: number;
}) {
  const colors = ["bg-red-600", "bg-red-400", "bg-red-200"];
  const textColors = ["text-white", "text-white", "text-red-700"];
  const bgColor = colors[Math.min(depth, 2)];
  const textColor = textColors[Math.min(depth, 2)];

  return (
    <div className="flex flex-col items-center">
      <div className="group relative">
        <div className={`${bgColor} rounded-2xl px-5 py-3 min-w-[120px] max-w-[200px] text-center shadow-sm`}>
          <p className={`text-xs ${textColor} font-medium opacity-80`}>{node.title}</p>
          <p className={`text-sm ${textColor} font-bold mt-0.5`}>{node.name}</p>
          {node.description?.trim() ? (
            <p
              className={`text-[10px] mt-1 leading-tight line-clamp-3 ${depth >= 2 ? "text-red-800" : "text-white/85"}`}
              title={node.description.trim()}
            >
              {node.description.trim()}
            </p>
          ) : null}
          <div className="absolute -top-2 -right-2 hidden group-hover:flex gap-1">
            <button
              type="button"
              onClick={() => onEdit(node)}
              className="w-6 h-6 flex items-center justify-center bg-white rounded-full shadow-md text-gray-600 hover:text-red-500 cursor-pointer transition-colors"
            >
              <i className="ri-edit-line text-xs"></i>
            </button>
            {depth < 2 && (
              <button
                type="button"
                onClick={() => onAdd(node.id)}
                className="w-6 h-6 flex items-center justify-center bg-white rounded-full shadow-md text-gray-600 hover:text-green-500 cursor-pointer transition-colors"
              >
                <i className="ri-add-line text-xs"></i>
              </button>
            )}
            {depth > 0 && (
              <button
                type="button"
                onClick={() => onDelete(node.id)}
                className="w-6 h-6 flex items-center justify-center bg-white rounded-full shadow-md text-gray-600 hover:text-red-500 cursor-pointer transition-colors"
              >
                <i className="ri-delete-bin-line text-xs"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {node.children && node.children.length > 0 && (
        <div className="flex flex-col items-center">
          <div className="w-px h-6 bg-gray-300"></div>
          <div className="flex gap-8 items-start relative">
            {node.children.length > 1 && (
              <div className="absolute top-0 left-1/2 -translate-x-1/2 h-px bg-gray-300 w-full"></div>
            )}
            {node.children.map((child) => (
              <div key={child.id} className="flex flex-col items-center">
                <div className="w-px h-6 bg-gray-300"></div>
                <OrgNodeCard
                  node={child}
                  onEdit={onEdit}
                  onAdd={onAdd}
                  onDelete={onDelete}
                  depth={depth + 1}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function OrgStructure() {
  const { tenantLegacyId, storeVersion } = useAdminData();
  const [org, setOrg] = useState<OrgNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingNode, setEditingNode] = useState<OrgNode | null>(null);
  const [saved, setSaved] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const raw = await fetchOrgStructureJson(tenantLegacyId);
      orgStructureStore.replaceFromApi(raw);
      setOrg(orgStructureStore.getRoots());
    } catch {
      orgStructureStore.replaceFromApi("[]");
      setOrg([]);
    } finally {
      setLoading(false);
    }
  }, [tenantLegacyId]);

  useEffect(() => {
    void reload();
  }, [reload, storeVersion]);

  const findAndUpdate = (nodes: OrgNode[], updated: OrgNode): OrgNode[] =>
    nodes.map((n) =>
      n.id === updated.id
        ? { ...updated, children: n.children }
        : { ...n, children: n.children ? findAndUpdate(n.children, updated) : undefined }
    );

  const findAndDelete = (nodes: OrgNode[], id: string): OrgNode[] =>
    nodes
      .filter((n) => n.id !== id)
      .map((n) => ({ ...n, children: n.children ? findAndDelete(n.children, id) : undefined }));

  const findAndAdd = (nodes: OrgNode[], parentId: string, newNode: OrgNode): OrgNode[] =>
    nodes.map((n) => {
      if (n.id === parentId) {
        return { ...n, children: [...(n.children || []), newNode] };
      }
      return { ...n, children: n.children ? findAndAdd(n.children, parentId, newNode) : undefined };
    });

  const handleSave = (updated: OrgNode) => {
    setOrg((p) => findAndUpdate(p, updated));
    setEditingNode(null);
  };

  const handleAdd = (parentId: string) => {
    const newNode: OrgNode = {
      id: `node-${Date.now()}`,
      title: "新职位",
      name: "待填写",
      level: 2,
      parentId,
    };
    setOrg((p) => findAndAdd(p, parentId, newNode));
  };

  /** 添加顶级节点（根）；首次默认「会长」，已有架构时再添加则为「新职位」；并打开编辑 */
  const handleAddRoot = () => {
    const isFirstRoot = org.length === 0;
    const newNode: OrgNode = {
      id: `node-${Date.now()}`,
      title: isFirstRoot ? "会长" : "新职位",
      name: "待填写",
      level: 0,
    };
    setOrg((p) => [...p, newNode]);
    setEditingNode(newNode);
  };

  const handleDelete = (id: string) => {
    setOrg((p) => findAndDelete(p, id));
  };

  const handleSaveAll = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const raw = JSON.stringify(org);
      await putOrgStructureJson(tenantLegacyId, raw);
      orgStructureStore.replaceFromApi(raw);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      reportError(e, "保存失败");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-gray-500 py-12 text-center">加载组织架构…</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-semibold text-gray-800">组织架构设置</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            鼠标悬停节点可编辑、添加或删除职位；无数据时请先添加根节点；完成后点击保存同步到服务端
          </p>
        </div>
        <div className="flex items-center gap-2">
          {org.length > 0 ? (
            <button
              type="button"
              onClick={() => handleAddRoot()}
              className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-node-tree"></i>
              添加顶级节点
            </button>
          ) : null}
        <button
          type="button"
          onClick={() => void handleSaveAll()}
          disabled={saving}
          className={`flex items-center gap-2 px-4 py-2 text-sm rounded-xl cursor-pointer transition-all whitespace-nowrap disabled:opacity-50 ${
            saved ? "bg-green-500 text-white" : "bg-red-600 text-white hover:bg-red-700"
          }`}
        >
          <i className={saved ? "ri-check-line" : "ri-save-line"}></i>
          {saved ? "已保存" : saving ? "保存中…" : "保存架构"}
        </button>
        </div>
      </div>

      <div className="bg-gray-50 rounded-2xl p-8 overflow-x-auto">
        <div className="flex justify-center min-w-max">
          {org.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 px-6 text-center max-w-md mx-auto">
              <i className="ri-organization-chart text-4xl text-gray-300 mb-3" aria-hidden />
              <p className="text-sm text-gray-600 mb-1">尚未配置组织架构</p>
              <p className="text-xs text-gray-400 mb-5">
                请先添加顶级职位（如会长）作为根节点，再通过节点上的「+」添加下级职位。
              </p>
              <button
                type="button"
                onClick={() => handleAddRoot()}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-xl bg-red-600 text-white hover:bg-red-700 cursor-pointer shadow-sm"
              >
                <i className="ri-add-line text-lg"></i>
                添加根节点
              </button>
            </div>
          ) : (
            org.map((node) => (
              <OrgNodeCard
                key={node.id}
                node={node}
                onEdit={setEditingNode}
                onAdd={handleAdd}
                onDelete={handleDelete}
              />
            ))
          )}
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 text-xs text-gray-400">
        <i className="ri-information-line mt-0.5"></i>
        <span>悬停节点显示操作按钮：<strong className="text-gray-500">编辑</strong>可维护职位、姓名与<strong className="text-gray-500">职责</strong>，<strong className="text-gray-500">+</strong>添加下级职位，<strong className="text-gray-500">删除</strong>该节点（仅限非会长职位）；职责最多 {DUTY_MAX_LEN} 字。</span>
      </div>

      {editingNode && (
        <EditModal node={editingNode} onSave={handleSave} onClose={() => setEditingNode(null)} />
      )}
    </div>
  );
}
